import { memo, useCallback } from 'react';

import { Button, CopyText, Flex, Heading, Label, Link, Text } from '@/components/core';
import { InfoIcon } from '@/components/icons';
import { ZapierLogo } from '@/components/logos';
import { toAppClientId, useGetAppClient, useListAppClients } from '@/hooks/api';

export type TriggerZapierConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const ITriggerZapierConfiguration = ({}: TriggerZapierConfigurationProps) => {
  const { data: appClients } = useListAppClients();
  const appClientWithSecret = appClients?.data?.find((appClient) => appClient.hasSecret);

  const { data: appClient } = useGetAppClient(toAppClientId(appClientWithSecret?.appClientId), {
    refetchInterval: (query) => (!query.state.data?.clientSecret ? 2000 : undefined),
  });

  const onSetupClicked = useCallback(() => {
    window.open('https://docs.cradl.ai/integrations/zapier', '_blank');
  }, []);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex col className="gap-sm">
        <Label>Account</Label>

        <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
          <ZapierLogo size="xl" />

          <Heading className="grow">Zapier</Heading>

          <Button variant="secondary" onPress={onSetupClicked}>
            <Text>Setup</Text>
          </Button>
        </Flex>
      </Flex>

      <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
        <Flex col className="gap-xs">
          <Label>Client ID</Label>
          <CopyText value={appClient?.clientId ?? 'Loading ...'} />
        </Flex>

        <Flex col className="gap-xs">
          <Label>Client Secret</Label>
          <CopyText isSensitive value={appClient?.clientSecret ?? 'Loading ...'} />
        </Flex>
      </Flex>

      <div className="grow" />

      <Flex col className="group/tutorial-box mb-md rounded-lg border-1 border-gray-200 bg-gray-50 shadow-md">
        <Flex className="p-sm items-center">
          <Heading size="sm" className="gap-xs flex grow">
            <InfoIcon className="text-gray-400" />
            Setting up Zapier
          </Heading>

          <Link href="https://docs.cradl.ai/integrations/zapier" className="font-regular text-sm">
            Read full guide
          </Link>
        </Flex>

        <Flex col className="p-md gap-sm rounded-lg border-1 border-gray-200 bg-white">
          <Text>
            1. Login to your <Link href="https://www.zapier.com">Zapier account</Link>.
          </Text>
          <Text>
            2. Add the <i>Extract data from document</i> action to your Zap.
          </Text>
          <Text>
            3. Setup a new <i>Account</i> using <i>Client ID</i> and <i>Client Secret</i> provided above.
          </Text>
          <Text>4. Publish your Zap.</Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const TriggerZapierConfiguration = memo(ITriggerZapierConfiguration) as typeof ITriggerZapierConfiguration;
